import queryString from "query-string"

import { API_URL } from "features/config"
import { saveFile } from "helpers/utils/csv"
import type { EntityType } from "types/common.types"
import type { CsvDownloadFormat, CsvDownloadGroup } from "types/csv.types"
import { zipCsvFiles } from "./zipCsvFiles"

export interface FetchedCsv {
  filename: string
  text: string
  nonEmpty: boolean
  emptyMessage?: string
}

interface GetUrlParamsProps {
  fromDate: string
  toDate: string
  orgId?: number | null
  entity?: EntityType
  fields?: string[]
  measurementOverride?: string
  geohashes?: string[]
}

const DEFAULT_MEASUREMENT: Partial<Record<EntityType, string>> = {
  powerline: "dlr",
  tower: "tower_health",
  span: "dlr",
}

const stripSlash = (value: string): string =>
  value.endsWith("/") ? value.slice(0, -1) : value

export const getUrl = ({
  id,
  entity = "device",
}: {
  id: number
  entity?: EntityType
}): string => {
  const base = stripSlash(API_URL)
  if (entity === "powerline") return `${base}/v1/powerlines/${id}/telemetry`
  if (entity === "tower") return `${base}/v1/powerlines_towers/${id}/telemetry`
  if (entity === "span") return `${base}/v1/powerlines_spans/${id}/telemetry`
  return `${base}/v1/devices/${id}/telemetry`
}

export const getWeatherUrl = (): string => `${stripSlash(API_URL)}/v1/weather_service`

export const getUrlParams = ({
  fromDate,
  toDate,
  orgId,
  entity = "device",
  fields,
  measurementOverride,
  geohashes,
}: GetUrlParamsProps): string => {
  const measurement = measurementOverride ?? DEFAULT_MEASUREMENT[entity]
  return queryString.stringify({
    from_date: fromDate,
    to_date: toDate,
    format: "csv",
    keep: ["_field", "_time", "_value"],
    ...(orgId != null && { org_id: orgId }),
    ...(measurement && { measurement }),
    ...(fields && fields.length > 0 && { field: fields }),
    ...(geohashes && geohashes.length > 0 && { geohash: geohashes }),
  })
}

const withFilenameSuffix = (baseFilename: string, suffix: string): string =>
  baseFilename.replace(".csv", `${suffix}.csv`)

const getMeasurementSuffix = (measurement: string | undefined): string =>
  measurement ? `_${measurement}` : ""

export const getWeatherCsvFilename = (
  baseFilename: string,
  group: CsvDownloadGroup,
): string =>
  withFilenameSuffix(
    baseFilename,
    `_weather_${group.geohash}${getMeasurementSuffix(group.measurement)}`,
  )

export const getEntityCsvFilename = (
  baseFilename: string,
  group: CsvDownloadGroup,
  groupId: number,
  needsSuffix: boolean,
): string =>
  needsSuffix
    ? withFilenameSuffix(
        baseFilename,
        `_${group.entity}_${groupId}${getMeasurementSuffix(group.measurement)}`,
      )
    : baseFilename

export const readCsvResponse = (
  response: Response,
  filename: string,
  emptyMessage?: string,
): Promise<FetchedCsv> => {
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.text().then((text) => {
    const dataLines = text.split("\n").filter((l) => l.trim().length > 0)
    return { filename, text, nonEmpty: dataLines.length >= 2, emptyMessage }
  })
}

/** Runs every task, then rethrows the first rejection so one failure fails the download. */
export const settleCsvTasks = async (
  tasks: Array<() => Promise<FetchedCsv>>,
): Promise<FetchedCsv[]> => {
  const results = await Promise.allSettled(tasks.map((fn) => fn()))
  const failed = results.filter((r) => r.status === "rejected")
  if (failed.length > 0) {
    throw (failed[0] as PromiseRejectedResult).reason
  }
  return (results as PromiseFulfilledResult<FetchedCsv>[]).map((r) => r.value)
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const saveCsvFiles = async (
  files: FetchedCsv[],
  format: CsvDownloadFormat,
  zipFilename: string,
): Promise<void> => {
  if (format === "zip") {
    if (files.length === 0) return

    // Encoding, compression and assembly all happen in our own worker: fflate's async
    // `zip` would build one from a `blob:` URL, which the production CSP blocks.
    const bytes = await zipCsvFiles(files)
    saveFile(new Blob([bytes as BlobPart], { type: "application/zip" }), zipFilename)
    return
  }
  // Save sequentially with a small gap so Chrome doesn't silently block the
  // burst of near-simultaneous downloads.
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i]
    saveFile(new Blob([file.text], { type: "text/csv" }), file.filename)
    if (i < files.length - 1) await delay(400)
  }
}
